/**
 * Chat-page display tokens and small derivations.
 *
 * Matches the pattern used by `bills-display.ts` / `circle-display.ts` /
 * `medical-display.ts`. Pure functions and tokens — no React. Anything
 * that needs the companion name at runtime should be passed in (this file
 * is imported by both React components and non-React utilities).
 */

import type { ChatMessageState } from '@/types/chat';
import { msg, type Msg } from '@/i18n/msg';
import { formatTime } from '@/i18n/intl';

/**
 * A chat turn's clock in the reader's UI language (#2109 R4): "3:04 PM" in
 * en, "15:04" in es — the locale's own hour cycle through the Intl `time`
 * preset, never date-fns `h:mm a`. An unparseable timestamp reads as ''.
 * Shared by ChatBubble and MessageList's staff-side counterpart turn.
 */
export function formatClock(timestamp: string): string {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? '' : formatTime(date);
}

/**
 * Background + ring tokens for each known agent. Used by the admin-only
 * agent pill on assistant bubbles and the conversation rail's agent dot.
 * Parents never see these — they see the configured companion name.
 */
export const AGENT_TOKENS: Record<
  string,
  { dot: string; pill: string; label: string }
> = {
  clio:        { dot: 'bg-stevens-indigo',  pill: 'text-stevens-indigo bg-stevens-indigo/10',  label: 'Clio' },
  guardian:    { dot: 'bg-cat-teal-fg',     pill: 'text-cat-teal-fg bg-cat-teal',       label: 'Guardian' },
  circlebot:   { dot: 'bg-cat-sky-fg',     pill: 'text-cat-sky-fg bg-cat-sky',        label: 'CircleBot' },
  financebot:  { dot: 'bg-cat-magenta-fg',  pill: 'text-cat-magenta-fg bg-cat-magenta', label: 'FinanceBot' },
  williams:    { dot: 'bg-chip-neutral-fg', pill: 'text-chip-neutral-fg bg-chip-neutral', label: 'Williams' },
  calliope:    { dot: 'bg-cat-violet-fg',  pill: 'text-cat-violet-fg bg-cat-violet',  label: 'Calliope' },
};

export const FALLBACK_AGENT_TOKEN = { dot: 'bg-stevens-rule-2', pill: 'text-stevens-ink-3 bg-stevens-bg-3' };

/**
 * Intent labels for admin pills. Map of backend intent slug → human label.
 * Add new intents here as the classifier learns them — falling back to the
 * raw slug if unknown so admins can still see what's happening.
 */
export const INTENT_LABEL: Record<string, string> = {
  task_management: 'Task',
  medical_question: 'Medical',
  bill_question: 'Bill',
  circle_management: 'Circle',
  emotional_support: 'Support',
  scheduling: 'Schedule',
  small_talk: 'Chat',
  escalation: 'Escalate',
  // unknown intents pass through unchanged
};

export function intentLabel(slug?: string | null): string | null {
  if (!slug) return null;
  return INTENT_LABEL[slug] ?? slug.replace(/_/g, ' ');
}

/**
 * Conversation rail state derivations.
 */
export interface ConversationDisplayState {
  /**
   * "Active now" if updated within the last minute, else a relative phrase,
   * or `null` with no usable timestamp. A message descriptor (#2109 R3-B3):
   * the component resolves it at render, so it follows a language switch.
   */
  lastActiveLabel: Msg | null;
  /** True if updated >24h ago — used to soften the rail entry visually. */
  isDormant: boolean;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function deriveConversationDisplayState(updatedAt: string | undefined): ConversationDisplayState {
  if (!updatedAt) return { lastActiveLabel: null, isDormant: false };
  const diffMs = Date.now() - new Date(updatedAt).getTime();
  if (Number.isNaN(diffMs) || diffMs < 0) return { lastActiveLabel: null, isDormant: false };
  const isDormant = diffMs > DAY;
  let lastActiveLabel: Msg;
  if (diffMs < MINUTE) lastActiveLabel = msg('chat:time.activeNow');
  else if (diffMs < HOUR) lastActiveLabel = msg('chat:time.minutesAgo', { count: Math.floor(diffMs / MINUTE) });
  else if (diffMs < DAY) lastActiveLabel = msg('chat:time.hoursAgo', { count: Math.floor(diffMs / HOUR) });
  else if (diffMs < 7 * DAY) lastActiveLabel = msg('chat:time.daysAgo', { count: Math.floor(diffMs / DAY) });
  else lastActiveLabel = msg('chat:time.overAWeek');
  return { lastActiveLabel, isDormant };
}

/**
 * Idle threshold for the Stevens callout in the empty state — 24h since the
 * last message in the active conversation. Re-used in multiple places.
 */
export const IDLE_THRESHOLD_MS = DAY;

export function isConversationIdle(messages: ChatMessageState[]): boolean {
  if (messages.length === 0) return true;
  const last = messages[messages.length - 1];
  if (!last.timestamp) return false;
  return Date.now() - new Date(last.timestamp).getTime() > IDLE_THRESHOLD_MS;
}
